import { usePublicLanguage } from './PublicLanguage';
import { publicRoute, languageTag, PUBLIC_LOCALES, PublicLocale, localizedPath } from './locales';
import { translateDocument, hasDocumentTranslation } from './translatedDocuments';
import { modalComponents } from '../utils/modalSurface';
import React, { useEffect, useMemo, useState } from 'react';
import LandingPage, { HOME_TITLE, HOME_DESCRIPTION } from '../pages/LandingPage';
import illustration from '../assets/landing-landscape-1600.webp';
import illustrationSmall from '../assets/landing-landscape-800.webp';
import illustrationMedium from '../assets/landing-landscape-1200.webp';
import { BlogPage } from '../pages/Blog';
import GamesPage, { GAMES_TITLE, GAMES_DESCRIPTION } from '../pages/GamesPage';
import FAQPage, { FAQ_TITLE, FAQ_DESCRIPTION, faqSchema } from '../pages/FAQPage';
import StaticPage, { StaticDocument } from './StaticPage';
import infoPages from '../content/info.json';
import archive from '../content/blog.json';
import legalPages from '../content/legal.json';
import { BLOG_TITLE, BLOG_DESCRIPTION, articlePath, articleSchema } from './blog';
import { GAME_PAGES, gamePath, resolveGameRoute } from './routes';
import { applyMetadata } from './metadata';
import logo from '../assets/revelationary-logo-compact.webp';
import Cookies from 'js-cookie';
import { parseReadingBookmark } from './readingEntry';
import CookieConsent from '../components/CookieConsent/CookieConsent';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const staticPages: StaticDocument[] = [...legalPages, ...infoPages];

function savedTheme(): boolean {
  try {
    return localStorage.getItem('darkMode') === 'true';
  } catch {
    return false;
  }
}

export default function PublicHome() {
  const { locale, messages, t, href } = usePublicLanguage();
  const [dark, setDark] = useState(savedTheme);
  const theme = useMemo(
    () => createTheme({ components: modalComponents, palette: { mode: dark ? 'dark' : 'light' } }),
    [dark],
  );
  let hasReadingHistory = false;
  try {
    hasReadingHistory = parseReadingBookmark(Cookies.get('bookmark')) !== null;
  } catch {
    /* Storage may be unavailable. */
  }
  const path = publicRoute(window.location.pathname).path.replace(/\/$/, '') || '/';
  const sourcePage = staticPages.find((page) => path === `/${page.slug}`);
  const page = useMemo(
    () => (sourcePage ? translateDocument(sourcePage, locale, messages) : undefined),
    [sourcePage, locale, messages],
  );
  const posts = useMemo(
    () => archive.posts.map((post) => translateDocument(post, locale, messages)),
    [locale, messages],
  );
  const gameRoute = resolveGameRoute(path, window.location.search);
  const gameKind = gameRoute?.kind;
  const gamePage = gameKind ? GAME_PAGES[gameKind] : null;
  const games = path === '/games' || !!gameKind;
  const faq = path === '/faq';
  const blog = path === '/blog' || path.startsWith('/blog/');
  const post = posts.find((article) => path === articlePath(article.slug).replace(/\/$/, ''));
  const missing = blog && path !== '/blog' && !post;
  useEffect(() => {
    const origin = 'https://revelationary.online';
    const canonicalPath = gameKind ? gamePath(gameKind) : path === '/' ? '/' : `${path}/`;
    const originalPost = archive.posts.find((item) => item.slug === post?.slug) || sourcePage;
    const translated = !originalPost || hasDocumentTranslation(originalPost, locale);
    const url = `${origin}${translated ? href(canonicalPath) : canonicalPath}`;
    const alternates = (Object.keys(PUBLIC_LOCALES) as PublicLocale[])
      .filter((code) => !originalPost || hasDocumentTranslation(originalPost, code))
      .map((code) => ({
        language: languageTag(code),
        url: `${origin}${localizedPath(canonicalPath, code)}`,
      }));
    const title = faq
      ? FAQ_TITLE
      : page
        ? `${page.title} | Revelationary`
        : post
          ? `${post.title} | Revelationary`
          : blog
            ? missing
              ? 'Article not found | Revelationary'
              : BLOG_TITLE
            : games
              ? gamePage
                ? `${gamePage.title} | Revelationary`
                : GAMES_TITLE
              : HOME_TITLE;
    const description = faq
      ? FAQ_DESCRIPTION
      : page
        ? page.description || page.title
        : post?.excerpt ||
          (blog
            ? BLOG_DESCRIPTION
            : games
              ? gamePage?.description || GAMES_DESCRIPTION
              : HOME_DESCRIPTION);
    applyMetadata({
      title: t(title),
      description: t(description),
      language: languageTag(locale),
      alternates,
      url,
      image: (gamePage ? `${origin}${gamePage.image}` : post?.image) || `${origin}${illustration}`,
      type: post ? 'article' : 'website',
      noindex: missing,
      schema: faq
        ? faqSchema(t, url, languageTag(locale))
        : missing
          ? undefined
          : post
            ? articleSchema(post, translated ? locale : 'en')
            : {
                '@context': 'https://schema.org',
                '@type': page
                  ? page.schemaType || 'WebPage'
                  : gamePage
                    ? 'WebPage'
                    : blog || games
                      ? 'CollectionPage'
                      : 'WebSite',
                name: t(title),
                description: t(description),
                inLanguage: languageTag(locale),
                url,
              },
    });
  }, [
    path,
    page,
    post,
    blog,
    missing,
    games,
    gameKind,
    gamePage,
    faq,
    locale,
    t,
    href,
    sourcePage,
  ]);
  const themeProps = {
    logo,
    dark,
    onToggleTheme: () => {
      setDark((previous) => {
        try {
          localStorage.setItem('darkMode', String(!previous));
        } catch {
          /* Theme still works for this visit. */
        }
        return !previous;
      });
    },
  };
  return (
    <ThemeProvider theme={theme}>
      {faq ? (
        <FAQPage {...themeProps} />
      ) : games ? (
        <GamesPage {...themeProps} game={gameKind} leaderboardView={gameRoute?.leaderboardView} />
      ) : page ? (
        <StaticPage page={page} {...themeProps} />
      ) : blog ? (
        <BlogPage posts={posts} post={post} missing={missing} {...themeProps} />
      ) : (
        <LandingPage
          articles={posts}
          illustration={illustration}
          illustrationSrcSet={`${illustrationSmall} 800w, ${illustrationMedium} 1200w, ${illustration} 1600w`}
          hasReadingHistory={hasReadingHistory}
          {...themeProps}
        />
      )}
      <CookieConsent />
    </ThemeProvider>
  );
}
